import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import { DrawerModuleSection } from 'common/components/DrawerModuleSection';
import {
    IPageDrawerProps,
    PageDrawer,
} from 'common/components/PageDrawer/PageDrawer';
import { useApolloError } from 'common/hooks/useApolloError';
import {
    getExtraFieldChoicesInitialValue,
    getExtraFieldInitialEmptyValues,
} from 'common/utils/userExtraFields';
import {
    IGroupListItemFragment,
    ILanguage,
    IRole,
    IUpdateProfileInput,
    IUpdateUserInput,
    IUserNotifications,
    CurrentUserDocument,
    ExtraUserFieldsDocument,
    OrganisationDocument,
    UpdateProfileDocument,
    UpdateUserDocument,
    UserDocument,
    IBasicFileFragment,
} from 'graphql/types';
import { UserPreview } from 'user/components/UserPreview';
import { getExtraFieldsName } from 'common/utils/extraFields';
import { getImageId } from 'common/utils/image';
import { UserUpdateForm } from 'user/components/forms/UserUpdateForm';
import { IGlobalDrawerType } from 'common/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { useCanManageUser } from 'user/hooks';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { getAddedGroupIds, getRemovedGroupIds } from 'user/utils/groups';
import { TFrontendRole } from 'user/types';
import { i18n } from 'utils';

export interface IUserFormValues extends IUpdateUserInput {
    participantGroups?: IGroupListItemFragment[];
    managerGroups?: IGroupListItemFragment[];
}

interface IProps extends IPageDrawerProps {
    userId: string;
    onArchive?(): void;
    onUpdate?(initialValues: IUserFormValues, newValues: IUserFormValues): void;
}

export const UserUpdateDrawer = ({
    userId,
    onArchive,
    onUpdate,
    ...other
}: IProps) => {
    const { showApolloError } = useApolloError();
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();

    const {
        data: extraUserFieldsData,
        loading: extraUserFieldsLoading,
        error: extraUserFieldsError,
    } = useQuery(ExtraUserFieldsDocument);

    useEffect(() => {
        if (extraUserFieldsError) {
            showApolloError(extraUserFieldsError);
        }
    }, [extraUserFieldsError, showApolloError]);
    const [profileImage, setProfileImage] = useState<IBasicFileFragment>();
    const [loadingUpload, setLoadingUpload] = useState(false);
    const [submittedValues, setSubmittedValues] = useState<IUserFormValues>();
    const { closeGlobalDrawer } = useGlobalDrawer();
    const [updateUser, { loading: updateUserLoading }] =
        useMutation(UpdateUserDocument);
    const [updateProfile] = useMutation(UpdateProfileDocument);
    const extraUserFields = extraUserFieldsData?.extraUserFields || [];
    const { data: userData, loading: userDataLoading } = useQuery(
        UserDocument,
        {
            variables: { id: userId },
        }
    );
    const { data: organisationData } = useQuery(OrganisationDocument);

    const hideUserEmail =
        organisationData?.organisation?.hideUserEmail || false;
    const allowUserEmailChange =
        organisationData?.organisation?.allowUserEmailChange ?? true;

    const {
        canManageUser,
        isCurrentUser,
        userRoles,
        loading: loadingCanManageUser,
    } = useCanManageUser(userId);

    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const { roles: currentUserRoles, sourceOrganisation } =
        currentUserData?.currentUser || {};
    const isGhostUser = !!sourceOrganisation;

    const { user } = userData || {};
    const {
        name,
        email,
        roles,
        notifications,
        language,
        extraFieldsProfile,
        participantGroups,
        managerGroups,
        ref,
    } = user || {};

    const extraFieldsParsed = extraFieldsProfile
        ? JSON.parse(extraFieldsProfile)
        : {};

    const isManager = currentUserRoles?.includes(IRole.Manager);

    const initialExtraFields = extraUserFields.length
        ? Object.keys(extraFieldsParsed).reduce((acc, curKey) => {
              const currentValue = extraFieldsParsed[curKey];

              if (!currentValue) return acc;

              const result = extraUserFields.filter(
                  (extraField) => extraField.name === curKey
              );

              // If field is choice field and value is string make it an array value
              if (result.length) {
                  return {
                      ...acc,
                      [getExtraFieldsName(curKey as string)]:
                          getExtraFieldChoicesInitialValue(
                              result[0],
                              currentValue
                          ),
                  };
              }

              return { ...acc, [curKey]: extraFieldsParsed[curKey] };
          }, {})
        : {};

    const initialValues: IUserFormValues = {
        name: name || '',
        email: email || '',
        roles: roles?.filter(Boolean) || [],
        notifications: notifications || IUserNotifications.Immediate,
        language: language || ILanguage.Nl,
        participantGroups: participantGroups || undefined,
        managerGroups: managerGroups || undefined,
        ref: isManager ? ref : undefined,
        ...getExtraFieldInitialEmptyValues(extraUserFields),
        ...initialExtraFields,
    };

    const isExternalUser = !!user?.userVia?.length;

    // User cannot change email when user is external or allowUserEmailChange is false and
    // user is current user and is not manager.
    const emailFieldDisabled =
        (isExternalUser || !allowUserEmailChange) &&
        isCurrentUser &&
        !isManager;

    useEffect(() => {
        if (!!profileImage) return;

        setProfileImage(user?.profileImage);
    }, [userDataLoading, user?.profileImage]);

    const handleSubmit = async (userFormValues: IUserFormValues) => {
        let userValues: IUserFormValues = { ...userFormValues };

        if (extraUserFields.length) {
            const extraFields: Record<string, unknown> = {};

            extraUserFields.forEach((field) => {
                const fieldKey = getExtraFieldsName(
                    field.name
                ) as keyof IUserFormValues;

                // If you can manage or if you can update your own field
                if ((isCurrentUser && field.profile) || canManageUser) {
                    // Retransform field name to its original field name
                    extraFields[field.name] =
                        userValues[fieldKey as keyof IUserFormValues];
                }

                delete userValues[fieldKey];
            });

            if (Object.keys(extraFields).length) {
                userValues = {
                    ...userValues,
                    extraFields: JSON.stringify(extraFields),
                };
            }
        }

        try {
            let updatedUser;
            const { participantGroups, managerGroups, ...otherUserValues } =
                userValues;

            const addParticipantGroups = getAddedGroupIds(
                initialValues.participantGroups || [],
                participantGroups || []
            );

            const removeParticipantGroups = getRemovedGroupIds(
                initialValues.participantGroups || [],
                participantGroups || []
            );

            const addManagerGroups = getAddedGroupIds(
                initialValues.managerGroups || [],
                managerGroups || []
            );

            const removeManagerGroups = getRemovedGroupIds(
                initialValues.managerGroups || [],
                managerGroups || []
            );

            // When profile is of the current user call update profile mutation
            if (isCurrentUser) {
                const {
                    name,
                    extraFields,
                    language: newLanguage,
                    notifications,
                    email,
                    afasContactId,
                    afasEmployeeId,
                    afasPersonId,
                    roles,
                    ref,
                } = userValues;

                const isOwnerOrManager =
                    userRoles?.includes(TFrontendRole.Owner) || isManager;

                const profileValues: IUpdateProfileInput = {
                    name,
                    extraFields,
                    language: newLanguage,
                    notifications,
                    email:
                        emailFieldDisabled || isGhostUser ? undefined : email,
                };

                if (isOwnerOrManager) {
                    profileValues.roles = roles;
                    profileValues.addParticipantGroups = addParticipantGroups;
                    profileValues.removeParticipantGroups =
                        removeParticipantGroups;
                    profileValues.addManagerGroups = addManagerGroups;
                    profileValues.removeManagerGroups = removeManagerGroups;
                }

                if (isManager) {
                    profileValues.ref = ref;

                    profileValues.afasContactId = afasContactId;
                    profileValues.afasEmployeeId = afasEmployeeId;
                    profileValues.afasPersonId = afasPersonId;
                }

                const response = await updateProfile({
                    variables: {
                        profile: {
                            ...profileValues,
                            profileImage: getImageId(
                                user?.profileImage,
                                profileImage
                            ),
                        },
                    },
                });

                updatedUser = response.data?.updateProfile?.user;

                // If language is changed, reload the page to apply the new language throughout the app
                if (!!newLanguage && newLanguage !== language) {
                    i18n.changeLanguage(newLanguage);
                }
            } else {
                // Remove email field if organisation has hideUserEmail enabled
                // or when the current viewer is a ghost user
                if (hideUserEmail || isGhostUser) delete otherUserValues.email;

                const response = await updateUser({
                    variables: {
                        id: userId,
                        user: {
                            ...otherUserValues,
                            profileImage: getImageId(
                                user?.profileImage,
                                profileImage
                            ),
                            addParticipantGroups,
                            removeParticipantGroups,
                            addManagerGroups,
                            removeManagerGroups,
                        },
                    },
                    update: (cache, mutationResult) => {
                        const { data: updatedUserData } = mutationResult;

                        const updatedUser = updatedUserData?.updateUser?.user;

                        if (!updatedUser) return;

                        cache.evict({ id: `User:${userId}` });
                        cache.gc();
                    },
                });
                updatedUser = response.data?.updateUser?.user;
            }

            if (!updatedUser) return;

            // Update profile image in state after successful update
            setProfileImage(updatedUser?.profileImage);

            onUpdate?.(initialValues, userFormValues);
        } catch (error) {
            showApolloError(error);

            // Save submitted values. If backend throws an error we do not want to lose the values
            setSubmittedValues(userFormValues);

            return;
        }

        displaySnackbar(
            translate(
                `userActionSuccess.${isCurrentUser ? 'profile' : 'update'}`
            ),
            {
                variant: 'success',
            }
        );

        closeGlobalDrawer({
            type: IGlobalDrawerType.UserUpdate,
        });

        // Reset submitted values as the form was successfully submitted
        if (submittedValues) setSubmittedValues(undefined);

        return;
    };

    const loading =
        extraUserFieldsLoading || updateUserLoading || loadingCanManageUser;

    return (
        <PageDrawer {...other}>
            <DrawerModuleSection>
                <UserPreview
                    name="profileImage"
                    profileImage={profileImage}
                    onUpload={setProfileImage}
                    onUploadFinish={() => setLoadingUpload(false)}
                    onUploadStart={() => setLoadingUpload(true)}
                />
            </DrawerModuleSection>

            {!!user && (
                <UserUpdateForm
                    canManage={canManageUser}
                    disabled={loadingUpload}
                    emailFieldDisabled={emailFieldDisabled}
                    emailFieldHidden={
                        (hideUserEmail && !isCurrentUser) || isGhostUser
                    }
                    extraFields={extraUserFields}
                    imageUploaded={profileImage?.id !== user?.profileImage.id}
                    initialValues={initialValues}
                    isCurrentUser={isCurrentUser}
                    loading={loading}
                    user={user}
                    onArchive={onArchive}
                    onClose={() =>
                        closeGlobalDrawer({
                            type: IGlobalDrawerType.UserUpdate,
                        })
                    }
                    onSubmit={handleSubmit}
                />
            )}
        </PageDrawer>
    );
};
